/** Toast notification hook with module-level state management. */

import { useSyncExternalStore, useCallback } from 'react'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface ToastMessage {
  id: string
  variant: ToastVariant
  message: string
  duration: number
}

const MAX_TOASTS = 5

let toasts: ToastMessage[] = []
let nextId = 1
const listeners = new Set<() => void>()

function emitChange() {
  for (const listener of listeners) {
    listener()
  }
}

function getSnapshot(): ToastMessage[] {
  return toasts
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function addToast(variant: ToastVariant, message: string, duration = 5000): string {
  const id = String(nextId++)
  const toast: ToastMessage = { id, variant, message, duration }

  // Enforce max toasts — drop oldest if at capacity
  if (toasts.length >= MAX_TOASTS) {
    toasts = [...toasts.slice(1), toast]
  } else {
    toasts = [...toasts, toast]
  }

  emitChange()
  return id
}

function dismiss(id: string) {
  toasts = toasts.filter((t) => t.id !== id)
  emitChange()
}

function dismissAll() {
  toasts = []
  emitChange()
}

export interface ToastAPI {
  success: (message: string, duration?: number) => string
  error: (message: string, duration?: number) => string
  warning: (message: string, duration?: number) => string
  info: (message: string, duration?: number) => string
}

export interface UseToastReturn {
  toasts: ToastMessage[]
  toast: ToastAPI
  dismiss: (id: string) => void
  dismissAll: () => void
}

export function useToast(): UseToastReturn {
  const currentToasts = useSyncExternalStore(subscribe, getSnapshot)

  const toast: ToastAPI = useCallback(
    () => ({
      success: (message: string, duration?: number) => addToast('success', message, duration),
      error: (message: string, duration?: number) => addToast('error', message, duration),
      warning: (message: string, duration?: number) => addToast('warning', message, duration),
      info: (message: string, duration?: number) => addToast('info', message, duration),
    }),
    [],
  )()

  return {
    toasts: currentToasts,
    toast,
    dismiss,
    dismissAll,
  }
}

// Exported for testing: reset state between tests
export function _resetToasts() {
  toasts = []
  nextId = 1
  emitChange()
}
